import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  Crown, Trash2, TrendingUp, TrendingDown, ArrowLeftRight, Armchair, Zap, Trophy, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import TeamDetailModal from "@/components/app/TeamDetailModal";
import { T } from "@/lib/testIds";
import { cn } from "@/lib/utils";

const ICON = {
  crown: Crown,
  trash: Trash2,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  swap: ArrowLeftRight,
  armchair: Armchair,
  zap: Zap,
};

const TONE = {
  green: {
    ring: "border-totem-green/40",
    icon: "bg-totem-green/15 text-totem-green",
    value: "text-totem-green",
    glow: "0 0 18px rgba(0,255,133,0.12)",
  },
  gold: {
    ring: "border-totem-gold/40",
    icon: "bg-totem-gold/15 text-totem-gold",
    value: "text-totem-gold",
    glow: "0 0 18px rgba(227,195,43,0.14)",
  },
  amber: {
    ring: "border-totem-amber/40",
    icon: "bg-totem-amber/15 text-totem-amber",
    value: "text-totem-amber",
    glow: "0 0 18px rgba(255,184,0,0.10)",
  },
  red: {
    ring: "border-totem-red/40",
    icon: "bg-totem-red/15 text-totem-red",
    value: "text-totem-red",
    glow: "0 0 18px rgba(255,46,85,0.10)",
  },
};

function AwardCard({ award, isMe, onOpenManager }) {
  const Icon = ICON[award.icon] || Sparkles;
  const tone = TONE[award.tone] || TONE.green;
  const mgr = award.manager;
  return (
    <div
      data-testid={`roast-award-${award.key}`}
      className={cn("totem-card p-4 border", tone.ring, isMe && "ring-2 ring-totem-green/60")}
      style={{ boxShadow: tone.glow }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={cn("grid place-items-center w-9 h-9 rounded-lg", tone.icon)}>
          <Icon size={16} />
        </span>
        <div className="text-right">
          <div className={cn("display-heading text-2xl leading-none", tone.value)}>
            {award.value}
          </div>
          {award.value_suffix && (
            <div className="mono text-[9px] uppercase text-totem-textDim mt-0.5">
              {award.value_suffix}
            </div>
          )}
        </div>
      </div>
      <div className="display-heading text-base leading-tight">{award.title}</div>
      <div className="mono text-[10px] uppercase text-totem-textDim mt-0.5">
        {award.subtitle}
      </div>
      <button
        type="button"
        onClick={() => mgr?.entry && onOpenManager?.(mgr)}
        disabled={!mgr?.entry}
        data-testid={`roast-award-tap-${award.key}`}
        className={cn(
          "mt-3 pt-3 border-t border-totem-borderSoft w-full text-left",
          mgr?.entry && "hover:bg-totem-surfaceHigh -mx-4 -mb-4 px-4 pb-4 rounded-b-lg transition"
        )}
      >
        <div className="flex items-center gap-2">
          {award.extra?.player && (
            <PlayerAvatar player={award.extra.player} size={28} />
          )}
          <div className="min-w-0 flex-1">
            <div className={cn("font-semibold text-sm truncate", isMe && "text-totem-green")}>
              {isMe ? "You" : mgr?.entry_name}
              {isMe && (
                <span className="ml-1 mono text-[9px] uppercase bg-totem-green/15 text-totem-green px-1 py-0.5 rounded">
                  YOU
                </span>
              )}
            </div>
            <div className="mono text-[10px] uppercase text-totem-textDim truncate">
              {mgr?.player_name}
              {award.extra?.player && ` • ${award.extra.player.web_name}`}
              {award.extra?.chip && ` • ${award.extra.chip}`}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}

export default function LeagueDashboard({ leagueId }) {
  const { id: paramId } = useParams();
  const lid = leagueId || paramId;
  const [selected, setSelected] = useState(null);
  const openManager = (mgr) =>
    setSelected({ entry: mgr.entry, entry_name: mgr.entry_name, player_name: mgr.player_name });
  const { data, isLoading, error } = useQuery({
    queryKey: ["league-dashboard", lid],
    queryFn: async () => (await api.get(`/leagues/${lid}/dashboard`)).data,
    staleTime: 60_000,
  });

  if (isLoading) return <div className="totem-card h-72 animate-pulse" />;
  if (error) return <div className="totem-card p-6 text-totem-red">{String(error.message)}</div>;

  const awards = data.awards || [];
  const leader = data.leader;
  const me = data.user_manager_id;

  return (
    <div data-testid={T.leagueDashboardPage} className="space-y-4 animate-fade-up">
      {leader && (
        <button
          type="button"
          onClick={() => openManager(leader)}
          data-testid="league-leader-tap"
          className="w-full text-left totem-card p-5 border-totem-green/30 hover:border-totem-green/60 transition"
          style={{ boxShadow: "0 0 20px rgba(0,255,133,0.10)" }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="text-totem-green" size={16} />
              <span className="mono uppercase text-[10px] tracking-widest text-totem-green">
                League leader
              </span>
            </div>
            <span className="mono text-[10px] uppercase text-totem-textDim">
              GW{data.gameweek}
            </span>
          </div>
          <div className="flex items-end justify-between">
            <div className="min-w-0">
              <div className="display-heading text-2xl truncate">{leader.entry_name}</div>
              <div className="mono text-xs text-totem-textDim uppercase mt-1 truncate">
                {leader.player_name}
              </div>
            </div>
            <div className="text-right">
              <div className="display-heading text-3xl text-totem-green">{leader.total}</div>
              <div className="mono text-[10px] uppercase text-totem-textDim mt-1">
                {leader.event_total ?? 0} this GW
              </div>
            </div>
          </div>
        </button>
      )}

      {data.message ? (
        <section className="totem-card p-6 text-center border-totem-amber/30">
          <Sparkles className="mx-auto text-totem-amber mb-2" size={22} />
          <div className="display-heading text-lg mb-1">Pre-season</div>
          <p className="text-sm text-totem-textDim leading-relaxed">{data.message}</p>
        </section>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="mono uppercase text-[10px] tracking-widest text-totem-gold flex items-center gap-1">
              <Sparkles size={12} /> Roast Awards
            </span>
            <span className="text-totem-textFaint text-[10px] mono">•</span>
            <span className="mono text-[10px] uppercase text-totem-textDim">
              GW{data.gameweek} · top {data.total_managers}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {awards.length === 0 ? (
              <div className="totem-card p-6 text-center text-totem-textDim sm:col-span-2">
                No awards yet — check back after the GW finalises.
              </div>
            ) : (
              awards.map((a) => (
                <AwardCard
                  key={a.key}
                  award={a}
                  isMe={a.manager?.entry === me}
                  onOpenManager={openManager}
                />
              ))
            )}
          </div>

          {data.chip_watch?.length > 0 && (
            <section className="totem-card p-4">
              <div className="label-caps mb-2 flex items-center gap-2">
                <Zap className="text-totem-gold" size={12} /> Chip watch
              </div>
              <ul className="space-y-1.5">
                {data.chip_watch.map((c) => (
                  <li key={c.entry}>
                    <button
                      type="button"
                      onClick={() => openManager(c)}
                      data-testid={`chip-watch-tap-${c.entry}`}
                      className="w-full flex items-center justify-between text-sm px-2 py-1.5 -mx-2 rounded hover:bg-totem-surfaceHigh transition"
                    >
                      <span className="truncate text-left">
                        {c.entry_name}
                        {c.entry === me && (
                          <span className="ml-1 mono text-[9px] uppercase text-totem-green">(YOU)</span>
                        )}
                      </span>
                      <span className="mono text-[10px] uppercase text-totem-gold bg-totem-gold/10 border border-totem-gold/40 rounded-full px-2 py-0.5">
                        {c.chip}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      <TeamDetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        managerId={selected?.entry}
        entryName={selected?.entry_name}
        playerName={selected?.player_name}
      />
    </div>
  );
}
